/**
 * Decision trail for demos.
 * audience="citizen" — plain language, no skill/tool IDs.
 * audience="operator" (default) — CLAW skill + tool trail for Studio storytelling.
 */
import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { friendlyToolLabel } from '../../utils/citizenFriendlyLabels';

const STATUS_SX = {
  ok: { bgcolor: '#ecfdf5', color: '#065f46' },
  ready: { bgcolor: '#eff6ff', color: '#1e3a8a' },
  needs_input: { bgcolor: '#fff7ed', color: '#9a3412' },
  skipped: { bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary' },
  error: { bgcolor: '#fef2f2', color: '#991b1b' },
};

const STATUS_FRIENDLY = {
  ok: 'Done',
  ready: 'Ready',
  needs_input: 'Needs your input',
  skipped: 'Skipped',
  error: 'Needs attention',
};

export function downloadHandoffPacket(packet, filename) {
  if (!packet) return;
  const blob = new Blob([JSON.stringify(packet, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `${packet.type || 'packet'}-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function studioLiveHref(claw) {
  const live = claw?.studioLivePath || claw?.studioPath || claw?.helpPath || '/studio/apps';
  const signedIn = typeof window !== 'undefined' && Boolean(localStorage.getItem('tkn'));
  if (signedIn) return live;
  return `/signin?next=${encodeURIComponent(live)}`;
}

export default function ClawDecisionPanel({
  claw = null,
  handoffPacket = null,
  accent = '#0f766e',
  defaultOpen = false,
  audience = 'operator',
}) {
  const [open, setOpen] = useState(defaultOpen);
  const liveHref = useMemo(() => studioLiveHref(claw), [claw]);
  const citizen = audience === 'citizen';

  if (!claw?.skillId) return null;

  const tools = Array.isArray(claw.tools) ? claw.tools : [];
  const decision = claw.decision || {};
  const decisionChips = Object.entries(decision)
    .filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length))
    .slice(0, 8);

  const fileStem = handoffPacket?.type === 'rma_packet_v1'
    ? `rma-${handoffPacket?.order?.orderId || 'packet'}`
    : handoffPacket?.type === 'screening_scorecard_v1'
      ? `scorecard-${(handoffPacket?.candidate?.name || 'candidate').replace(/\s+/g, '-').toLowerCase()}`
      : handoffPacket?.type === 'personal_assistant_packet_v1'
        ? `northstar-${handoffPacket?.intent || 'assist'}`
        : citizen ? 'application-summary' : 'claw-packet';

  const signedIn = typeof window !== 'undefined' && Boolean(localStorage.getItem('tkn'));

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 2,
        border: '1px dashed rgba(0,0,0,0.14)',
        bgcolor: 'rgba(255,255,255,0.55)',
        overflow: 'hidden',
      }}
      data-testid="claw-decision-panel"
      data-audience={audience}
    >
      <Button
        fullWidth
        onClick={() => setOpen((v) => !v)}
        sx={{
          justifyContent: 'space-between',
          textTransform: 'none',
          px: 1.5,
          py: 1.1,
          color: 'text.primary',
          borderRadius: 0,
        }}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <HubOutlinedIcon sx={{ color: accent, fontSize: 20 }} />
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {citizen
                ? 'How we figured this out'
                : `How this decided · CLAW${claw.mode === 'catalog' ? ' · preview' : ''}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {citizen
                ? (tools.length
                  ? `${tools.length} step${tools.length === 1 ? '' : 's'} from your answers`
                  : 'Based on your answers and city rules')
                : (
                  <>
                    Skill
                    {' '}
                    <code>{claw.skillId}</code>
                    {claw.mode === 'catalog' ? ' · catalog engine' : ''}
                    {tools.length ? ` · ${tools.length} tools` : ''}
                  </>
                )}
            </Typography>
          </Box>
        </Stack>
      </Button>

      <Collapse in={open}>
        <Divider />
        <Box sx={{ px: 1.75, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {citizen
              ? 'We matched your project to city rules, then listed what you still need. Guidance only — not an official determination.'
              : (claw.mode === 'catalog'
                ? (claw.studioHint
                  || 'Catalog engine + CLAW preview on this public path. Signed-in Studio runs the same skill with a live editable tool loop.')
                : (claw.studioHint
                  || 'Public demos run a deterministic advise path; signed-in Studio runs the same skill with an editable tool loop.'))}
          </Typography>
          {!citizen && claw.mode === 'catalog' && (
            <Chip
              size="small"
              label="Catalog advise · not live agent loop"
              sx={{ mb: 1.25, height: 22, fontSize: '0.7rem', bgcolor: 'rgba(0,0,0,0.05)' }}
              data-testid="claw-catalog-mode-chip"
            />
          )}

          {!citizen && decisionChips.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {decisionChips.map(([k, v]) => (
                <Chip
                  key={k}
                  size="small"
                  label={`${k}: ${Array.isArray(v) ? v.join(', ') : v}`}
                  sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.35 } }}
                />
              ))}
            </Stack>
          )}

          {tools.length > 0 && (
            <>
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.75 }}>
                {citizen ? 'What we checked' : 'Tool trail'}
              </Typography>
              <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                {tools.map((t, i) => {
                  const tone = STATUS_SX[t.status] || STATUS_SX.ok;
                  const label = citizen
                    ? friendlyToolLabel(t.name || t.id)
                    : (t.name || t.id);
                  return (
                    <Box
                      key={t.id || i}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-start',
                        bgcolor: 'rgba(255,255,255,0.8)',
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.85,
                        border: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          minWidth: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: accent,
                          color: '#fff',
                          display: 'grid',
                          placeItems: 'center',
                          fontWeight: 700,
                          mt: 0.15,
                        }}
                      >
                        {i + 1}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" fontWeight={600}>
                            {label}
                          </Typography>
                          <Chip
                            size="small"
                            label={citizen
                              ? (STATUS_FRIENDLY[t.status] || t.status || 'Done')
                              : (t.status || 'ok')}
                            sx={{ ...tone, height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                        {!citizen && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                            {t.id}
                          </Typography>
                        )}
                        {t.summary ? (
                          <Typography variant="body2" color="text.secondary">{t.summary}</Typography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
            {!citizen && (
              <Button
                size="small"
                variant="contained"
                component={RouterLink}
                to={liveHref}
                startIcon={<OpenInNewIcon />}
                sx={{
                  textTransform: 'none',
                  bgcolor: accent,
                  borderRadius: 2,
                  '&:hover': { filter: 'brightness(0.95)', bgcolor: accent },
                }}
                data-testid="claw-studio-live-cta"
              >
                {signedIn ? 'Run live skill in Studio' : 'Sign in · run live in Studio'}
              </Button>
            )}
            {handoffPacket ? (
              <Button
                size="small"
                variant={citizen ? 'contained' : 'outlined'}
                startIcon={<DownloadOutlinedIcon />}
                onClick={() => downloadHandoffPacket(handoffPacket, `${fileStem}.json`)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  ...(citizen
                    ? { bgcolor: accent, '&:hover': { filter: 'brightness(0.95)', bgcolor: accent } }
                    : { borderColor: accent, color: accent }),
                }}
                data-testid="claw-download-packet"
              >
                {citizen
                  ? 'Download your summary'
                  : (claw.handoff?.downloadHint || 'Download packet')}
              </Button>
            ) : null}
            {!citizen && claw.helpPath ? (
              <Button
                size="small"
                component={RouterLink}
                to={claw.helpPath}
                sx={{ textTransform: 'none', color: accent }}
              >
                CLAW details
              </Button>
            ) : null}
          </Stack>

          {!citizen && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Demonstrates {(claw.demonstrates || []).join(' · ') || 'skills'}
              {claw.helpPath ? (
                <>
                  {' · '}
                  <Link component={RouterLink} to={claw.helpPath} underline="hover">
                    same contract as this portal
                  </Link>
                </>
              ) : null}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
